/**
 * ManualBreadcrumb Component
 *
 * Breadcrumb navigation showing hierarchical path to current section.
 * Story 1-4: ManualBreadcrumb
 */

import { Link } from 'react-router-dom';
import type { BreadcrumbItem } from '../../lib/manualApi';

/**
 * ChevronRight separator icon (inline SVG)
 */
function ChevronSeparator({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

interface ManualBreadcrumbProps {
  /** Array of ancestor sections from root to direct parent */
  path: BreadcrumbItem[];
  /** Title of current section */
  currentTitle: string;
  /** Callback for navigation to ancestor section */
  onNavigate: (slug: string) => void;
}

/**
 * ManualBreadcrumb - Breadcrumb navigation for manual sections
 *
 * Shows hierarchical path: Handleiding > Parent 1 > Parent 2 > Current
 * All items except current are clickable for navigation.
 */
export function ManualBreadcrumb({ path, currentTitle, onNavigate }: ManualBreadcrumbProps) {
  return (
    <nav
      className="flex items-center flex-wrap gap-y-1 text-sm text-gray-600 mb-4"
      aria-label="Breadcrumb"
      data-testid="manual-breadcrumb"
    >
      <ol className="flex items-center flex-wrap" role="list">
        {/* Root link to handleiding overview */}
        <li className="flex items-center" data-testid="breadcrumb-item">
          <Link
            to="/handleiding"
            className="hover:text-blue-600 hover:underline transition-colors"
          >
            Handleiding
          </Link>
        </li>

        {/* Ancestor path items */}
        {path.map((item) => (
          <li key={item.slug} className="flex items-center" data-testid="breadcrumb-item">
            <ChevronSeparator className="h-4 w-4 mx-2 text-gray-400 flex-shrink-0" />
            <button
              type="button"
              onClick={() => onNavigate(item.slug)}
              className="hover:text-blue-600 hover:underline transition-colors truncate max-w-[200px]"
              title={item.title}
            >
              {item.title}
            </button>
          </li>
        ))}

        {/* Current section (not clickable) */}
        <li className="flex items-center" aria-current="page" data-testid="breadcrumb-item">
          <ChevronSeparator className="h-4 w-4 mx-2 text-gray-400 flex-shrink-0" />
          <span
            className="font-medium text-gray-900 truncate max-w-[250px]"
            title={currentTitle}
          >
            {currentTitle}
          </span>
        </li>
      </ol>
    </nav>
  );
}
